import React, { useState, useCallback, useSyncExternalStore } from 'react';
import { Bug, Loader2 } from 'lucide-react';
import AnnotationCanvas from './AnnotationCanvas';
import BugReportModal from './BugReportModal';
import { capturePage, describeCaptureError, newImageId, openReportCount, subscribeOpenReports } from '../lib/reportImages';

/**
 * ScreenshotCapture — Floating report button available on every authenticated page.
 * Flow: idle → capturing → annotating → form → idle
 *
 * The first capture opens the editor; Done or Cancel both lead into the form
 * (Cancel keeps the raw capture, #932). Further captures, attachments and
 * annotation happen inside the form (BugReportModal + ReportImageList).
 * When capture fails, the form opens with the reason and no image.
 *
 * Mounted once by RuntimeShell for every authenticated page.
 */
export default function ScreenshotCapture() {
    const [state, setState] = useState('idle'); // idle | capturing | annotating | form
    const [firstImage, setFirstImage] = useState(null);
    const [captureError, setCaptureError] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [capturedPageUrl, setCapturedPageUrl] = useState('');
    // Another report dialog (New Request) is open, or stepped aside to capture.
    const otherReportOpen = useSyncExternalStore(subscribeOpenReports, openReportCount) > 0;

    const capture = useCallback(async () => {
        setState('capturing');
        setCapturedPageUrl(window.location.href);
        setCaptureError(null);
        try {
            // Small delay to let the button hide
            await new Promise(r => setTimeout(r, 100));
            const dataUrl = await capturePage();
            setFirstImage({ id: newImageId(), dataUrl, source: 'capture' });
            setState('annotating');
        } catch (err) {
            console.error('Screenshot capture failed:', err);
            // Don't silently fail — open the form with the reason so the user
            // can attach an image or try again, and still submit.
            setFirstImage(null);
            setCaptureError(describeCaptureError(err));
            setState('form');
        }
    }, []);

    const handleAnnotationComplete = (annotatedDataUrl) => {
        setFirstImage(img => ({ ...img, dataUrl: annotatedDataUrl, source: 'annotated' }));
        setState('form');
    };

    const handleAnnotationCancel = () => setState('form');

    const handleFormClose = () => {
        setFirstImage(null);
        setCaptureError(null);
        setState('idle');
    };

    // Don't render button during capture or annotation
    if (state === 'capturing') {
        return (
            <div data-screenshot-capture="true" className="fixed bottom-20 right-6 z-50">
                <div className="w-12 h-12 rounded-full bg-stone-900 text-white flex items-center justify-center shadow-lg">
                    <Loader2 className="w-5 h-5 animate-spin" />
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Floating button — hidden during annotation, while the form is open, and
                while another report dialog is (so no second capture starts over it) */}
            {state === 'idle' && !otherReportOpen && (
                <div data-screenshot-capture="true"
                    className="fixed bottom-20 right-6 z-50"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}>
                    <button onClick={capture}
                        className="group flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all px-4 py-3"
                        title="Report Bug / Request Feature">
                        <Bug className="w-5 h-5" />
                        <span className={`text-sm font-medium overflow-hidden transition-all ${hovered ? 'max-w-[120px] opacity-100' : 'max-w-0 opacity-0'}`}>
                            Report
                        </span>
                    </button>
                </div>
            )}

            {/* Annotation overlay */}
            {state === 'annotating' && firstImage && (
                <AnnotationCanvas
                    screenshotDataUrl={firstImage.dataUrl}
                    onComplete={handleAnnotationComplete}
                    onCancel={handleAnnotationCancel}
                />
            )}

            {/* Bug report form */}
            {state === 'form' && (
                <BugReportModal
                    open={true}
                    onClose={handleFormClose}
                    initialImages={firstImage ? [firstImage] : []}
                    initialCaptureError={captureError}
                    pageUrl={capturedPageUrl}
                />
            )}
        </>
    );
}
